import { useState, useEffect, useCallback } from 'react';
import type { Firestore } from 'firebase/firestore';
import type { RSVP } from '@penticton-dev-events/shared';
import {
  subscribeToMeetingRSVPs,
  subscribeToPendingRSVPs,
  approveRSVP,
  rejectRSVP,
  batchApproveRSVPs,
} from '../api/rsvpRepository';

export interface UseRSVPManagementResult {
  // RSVP lists
  allRSVPs: RSVP[];
  pendingRSVPs: RSVP[];
  approvedRSVPs: RSVP[];
  rejectedRSVPs: RSVP[];

  // Loading states
  loading: boolean;
  approvingIds: Set<string>;
  rejectingIds: Set<string>;
  batchApproving: boolean;

  // Error state
  error: string | null;

  // Actions
  handleApprove: (userId: string) => Promise<void>;
  handleReject: (userId: string, reason?: string) => Promise<void>;
  handleBatchApprove: (userIds: string[]) => Promise<void>;
  clearError: () => void;
}

/**
 * Hook for managing RSVP approvals (organizer only)
 * Provides real-time RSVP lists and approval/rejection actions
 */
export const useRSVPManagement = (
  firestore: Firestore,
  meetingId: string | null,
  organizerUid: string | null,
): UseRSVPManagementResult => {
  const [allRSVPs, setAllRSVPs] = useState<RSVP[]>([]);
  const [pendingRSVPs, setPendingRSVPs] = useState<RSVP[]>([]);
  const [loading, setLoading] = useState(true);
  const [approvingIds, setApprovingIds] = useState<Set<string>>(new Set());
  const [rejectingIds, setRejectingIds] = useState<Set<string>>(new Set());
  const [batchApproving, setBatchApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Derive approved and rejected RSVPs from allRSVPs
  const approvedRSVPs = allRSVPs.filter(
    rsvp => rsvp.status === 'attending' && rsvp.approvalStatus === 'approved',
  );
  const rejectedRSVPs = allRSVPs.filter(rsvp => rsvp.approvalStatus === 'rejected');

  // Subscribe to all RSVPs for the meeting
  useEffect(() => {
    if (!meetingId || !organizerUid) {
      setAllRSVPs([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsubscribe = subscribeToMeetingRSVPs(
      firestore,
      meetingId,
      rsvps => {
        setAllRSVPs(rsvps);
        setLoading(false);
      },
      err => {
        console.error('Error subscribing to meeting RSVPs:', err);
        setError('Failed to load RSVPs');
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [firestore, meetingId, organizerUid]);

  // Subscribe to pending RSVPs
  useEffect(() => {
    if (!meetingId || !organizerUid) {
      setPendingRSVPs([]);
      return;
    }

    const unsubscribe = subscribeToPendingRSVPs(
      firestore,
      meetingId,
      rsvps => {
        setPendingRSVPs(rsvps);
      },
      err => {
        console.error('Error subscribing to pending RSVPs:', err);
        setError('Failed to load pending RSVPs');
      },
    );

    return () => unsubscribe();
  }, [firestore, meetingId, organizerUid]);

  const handleApprove = useCallback(
    async (userId: string) => {
      if (!meetingId || !organizerUid) {
        return;
      }

      setApprovingIds(prev => new Set(prev).add(userId));
      setError(null);

      try {
        await approveRSVP(firestore, meetingId, userId, organizerUid);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to approve RSVP';
        setError(message);
        throw err; // Re-throw for component error handling
      } finally {
        setApprovingIds(prev => {
          const next = new Set(prev);
          next.delete(userId);
          return next;
        });
      }
    },
    [firestore, meetingId, organizerUid],
  );

  const handleReject = useCallback(
    async (userId: string, reason?: string) => {
      if (!meetingId || !organizerUid) {
        return;
      }

      setRejectingIds(prev => new Set(prev).add(userId));
      setError(null);

      try {
        await rejectRSVP(firestore, meetingId, userId, organizerUid, reason);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to reject RSVP';
        setError(message);
        throw err; // Re-throw for component error handling
      } finally {
        setRejectingIds(prev => {
          const next = new Set(prev);
          next.delete(userId);
          return next;
        });
      }
    },
    [firestore, meetingId, organizerUid],
  );

  const handleBatchApprove = useCallback(
    async (userIds: string[]) => {
      if (!meetingId || !organizerUid || userIds.length === 0) {
        return;
      }

      setBatchApproving(true);
      setError(null);

      try {
        await batchApproveRSVPs(firestore, meetingId, userIds, organizerUid);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to batch approve RSVPs';
        setError(message);
        throw err; // Re-throw for component error handling
      } finally {
        setBatchApproving(false);
      }
    },
    [firestore, meetingId, organizerUid],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    allRSVPs,
    pendingRSVPs,
    approvedRSVPs,
    rejectedRSVPs,
    loading,
    approvingIds,
    rejectingIds,
    batchApproving,
    error,
    handleApprove,
    handleReject,
    handleBatchApprove,
    clearError,
  };
};
